import { create } from 'zustand'
import { api, WebPlayerUser } from '@/lib/api'

const WATCH_TOKEN_KEY = 'watch_access_token'
const WATCH_REFRESH_KEY = 'watch_refresh_token'
const WATCH_CREDS_KEY = 'watch_creds'
const WATCH_USER_KEY = 'watch_user'

interface WatchAuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: WebPlayerUser | null
  credentials: { username: string; password: string } | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => void
  getStreamUrl: (templateUrl: string) => string
}

export const useWatchAuth = create<WatchAuthState>((set, get) => ({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  credentials: null,

  login: async (username: string, password: string) => {
    // Clear any existing admin tokens first
    api.clearTokens()

    const data = await api.userLogin(username, password)
    if (!data.user.web_player_enabled) {
      api.clearTokens()
      throw new Error('Web player not enabled for this account')
    }

    // Store tokens in both watch-specific keys (for restoration) and api client keys (for requests)
    localStorage.setItem(WATCH_TOKEN_KEY, data.access_token)
    localStorage.setItem(WATCH_REFRESH_KEY, data.refresh_token)
    api.setTokens(data.access_token, data.refresh_token)

    // Store credentials for stream URL construction
    const creds = { username, password }
    console.log('[WatchAuth] login: storing credentials', { username })
    sessionStorage.setItem(WATCH_CREDS_KEY, JSON.stringify(creds))
    sessionStorage.setItem(WATCH_USER_KEY, JSON.stringify(data.user))
    set({ isAuthenticated: true, user: data.user, credentials: creds })
  },

  logout: () => {
    localStorage.removeItem(WATCH_TOKEN_KEY)
    localStorage.removeItem(WATCH_REFRESH_KEY)
    sessionStorage.removeItem(WATCH_CREDS_KEY)
    sessionStorage.removeItem(WATCH_USER_KEY)
    api.clearTokens()
    // Clear any stale state
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    set({ isAuthenticated: false, user: null, credentials: null })
  },

  checkAuth: () => {
    const token = localStorage.getItem(WATCH_TOKEN_KEY)
    const hasToken = !!token
    // Restore credentials from session storage
    const storedCreds = sessionStorage.getItem(WATCH_CREDS_KEY)
    const credentials = storedCreds ? JSON.parse(storedCreds) : null
    const storedUser = sessionStorage.getItem(WATCH_USER_KEY)
    const user = storedUser ? JSON.parse(storedUser) : null

    // Set the token in the API client if we have one
    if (token) {
      const refreshToken = localStorage.getItem(WATCH_REFRESH_KEY)
      if (refreshToken) {
        api.setTokens(token, refreshToken)
      }
    }

    console.log('[WatchAuth] checkAuth:', { hasToken, hasCredentials: !!credentials })
    set({ isAuthenticated: hasToken && !!credentials, isLoading: false, credentials, user })

    // Fetch fresh features in background to keep mosaic/recording flags current
    if (hasToken && credentials) {
      api.getAccountFeatures().then((features) => {
        const currentUser = get().user
        if (currentUser && features) {
          const updatedUser = {
            ...currentUser,
            mosaic_enabled: features.mosaic ?? false,
            max_mosaic_streams: features.max_mosaic_streams ?? 1,
            recording_enabled: features.recording ?? false,
            is_admin: features.is_admin ?? false,
          }
          sessionStorage.setItem(WATCH_USER_KEY, JSON.stringify(updatedUser))
          set({ user: updatedUser })
        }
      }).catch(() => {})  // Silently fail - cached user is still valid
    }
  },

  getStreamUrl: (templateUrl: string) => {
    const { credentials } = get()
    console.log('[WatchAuth] getStreamUrl:', { hasCredentials: !!credentials, templateUrl })
    if (!credentials) return templateUrl
    const url = templateUrl.replace('{password}', credentials.password)
    console.log('[WatchAuth] getStreamUrl result:', url)
    return url
  },
}))
